import React, {
  ChangeEvent,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import classNames from "classnames";
import styled, { css } from "styled-components";
import tw from "twin.macro";

import {
  EditableContent,
  EditableContentInterface,
} from "../EditableContent/EditableContent";
import { StyledDiv, StyledSpan } from "../EditableContent/Styles";
import { Typography } from "../Typography/Typography";

const StyledTextareaSpan = styled(Typography)`
  ${tw`
    tw-absolute
    tw-inset-0
    tw-border
    tw-border-transparent
    tw-border-solid
    hover:tw-border-theme-700
    focus-within:tw-border-theme-700
    tw-rounded-md
    tw-box-border
    tw-w-full`}

  background-color: var(--input-background-color, #ffffff);
  padding-left: 4px;
  padding-right: 4px;
  padding-top: 1px;
  padding-bottom: 1px;
  font-variant-numeric: tabular-nums;
  outline-width: 0px;
  outline: none;
  resize: none;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  height: calc(1.5em * 3 + 2px); /* 3 rows height: line-height * 3 + padding */
  min-height: calc(1.5em * 3 + 2px);
  opacity: 0;

  &:focus {
    opacity: 1;
    background-color: var(--input-background-color, #f8f9fa);
  }

  &:disabled {
    opacity: 0;
  }

  ${({ invalid }) =>
    invalid &&
    css`
      border-color: var(--red-alert) !important;
    `}
`;

export interface EditableTextInterface extends EditableContentInterface {
  /** Value to display. */
  value?: string | null;
  /** Optional callback called on enter, click outside and tab. */
  onSave?: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  /** Optional callback called on input reset. */
  onCancel?: () => void;
  /** If true, setting empty value will save it, instead of revert to previous value. */
  allowEmptyString?: boolean;
  /** If true, shows a textarea instead of input for multiline editing. */
  multiline?: boolean;
  /** Children content to display */
  children?: React.ReactNode;
}

export const EditableText = forwardRef<HTMLDivElement, EditableTextInterface>(
  (
    {
      onSave,
      onCancel,
      value,
      inputProps,
      allowEmptyString,
      multiline = false,
      variant = "Body 2",
      weight,
      disabled = false,
      invalid,
      wrapRef,
      children,
      ...props
    },
    ref
  ) => {
    const [currentValue, setCurrentValue] = useState(value);
    const [prevValue, setPrevValue] = useState(value);
    const escapeRef = useRef(false);
    const intTextareaRef = useRef<HTMLTextAreaElement>(null);
    const [isTextareaFocused, setIsTextareaFocused] = useState(false);

    useEffect(() => {
      if (currentValue !== value) {
        setCurrentValue(value);
        setPrevValue(value);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const handleBlur = useCallback(
      (e) => {
        setIsTextareaFocused(false);
        if (escapeRef.current) {
          setCurrentValue(prevValue);
          escapeRef.current = false;
        } else {
          if (
            e.target.value.trim().length > 0 &&
            prevValue !== e.target.value
          ) {
            setPrevValue(e.target.value);
            setCurrentValue(e.target.value);
            typeof onSave === "function" && onSave(e);
          } else {
            !allowEmptyString
              ? setCurrentValue(prevValue)
              : typeof onSave === "function" &&
                prevValue !== e.target.value &&
                onSave(e);
          }
        }
        // Reset scroll position for textarea
        if (multiline && intTextareaRef?.current) {
          intTextareaRef.current.scrollLeft = 0;
        }
      },
      [allowEmptyString, onSave, prevValue, multiline]
    );

    const handleFocus = useCallback(() => {
      setIsTextareaFocused(true);
    }, []);

    const handleKeyDown = useCallback(
      (e) => {
        if (e.key === "Enter" && !multiline) {
          e.target.blur();
        }
        if (e.key === "Escape") {
          escapeRef.current = true;
          e.target.blur();
          typeof onCancel === "function" && onCancel();
        }
      },
      [onCancel, multiline]
    );

    const handleChange = useCallback((e) => {
      setCurrentValue(e.target.value);
    }, []);

    if (multiline) {
      return (
        <StyledDiv ref={wrapRef} {...props}>
          <StyledSpan
            variant={variant}
            forwardedAs="span"
            weight={weight}
            $disabled={!isTextareaFocused}
            className="presentation"
          >
            {children
              ? children
              : String(currentValue || "").replace(/\n/g, " ")}
          </StyledSpan>
          <StyledTextareaSpan
            ref={intTextareaRef}
            forwardedAs="textarea"
            variant={variant}
            weight={weight}
            value={currentValue ?? ""}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            onChange={handleChange}
            disabled={disabled}
            invalid={invalid}
            data-form-type="other"
            placeholder={inputProps?.placeholder}
            className={classNames("c-textarea", inputProps?.className)}
            rows={3}
            {...inputProps}
          />
        </StyledDiv>
      );
    }

    return (
      <EditableContent
        {...props}
        variant={variant}
        weight={weight}
        disabled={disabled}
        invalid={invalid}
        wrapRef={wrapRef}
        ref={ref}
        inputProps={{
          ...inputProps,
          value: currentValue ?? "",
          onBlur: handleBlur,
          onKeyDown: handleKeyDown,
          onChange: handleChange,
          type: "text",
          className: classNames("c-input", inputProps?.className),
        }}
      >
        {children}
      </EditableContent>
    );
  }
);

EditableText.displayName = "EditableText";
